import {
  createFormatAdapter,
  createPlugin,
  wireHelper,
  type ParseGroupHook,
  type SerializeGroupHook,
} from '@customerio/dryad';
import { isRecord } from '@/utils/objects';

export const COLLECTION_ATTRIBUTE_CONDITION_TYPE = 'collection_attribute';
export const FLAT_FILTER_FORMAT = 'flat-filter';

export type AttributeFilterState = {
  field: string;
  operator: string;
  /**
   * The wire value, verbatim. For relative timestamp operators that is an offset
   * in seconds; the row shows it as whole days but never writes a day count
   * back unless the reader edits one, so an offset finer than a day survives.
   */
  value: string | null;
  value2: string | null;
};

type OperatorOption = { value: string; label: string; isDisabled?: boolean };
type OperatorGroup = { label: string; options: OperatorOption[] };

const stringOrNumberOperatorGroup = {
  label: 'String or number',
  options: [
    { label: 'is equal to', value: 'eq' },
    { label: 'is not equal to', value: '!eq' },
    { label: 'is greater than', value: 'gt' },
    { label: 'is less than', value: 'lt' },
    { label: 'is between', value: 'bw' },
    { label: 'exists', value: 'exists' },
    { label: 'does not exist', value: '!exists' },
  ],
} satisfies OperatorGroup;

const stringOperatorGroup = {
  label: 'String',
  options: [
    { label: 'contains', value: 'contains' },
    { label: 'does not contain', value: '!contains' },
  ],
} satisfies OperatorGroup;

const unixTimestampOperatorGroup = {
  label: 'Unix timestamp',
  options: [
    { label: 'is a timestamp', value: 'timestamp' },
    { label: 'is not a timestamp', value: '!timestamp' },
    { label: 'is a timestamp after', value: 'timestamp_gt' },
    { label: 'is a timestamp before', value: 'timestamp_lt' },
    { label: 'is a timestamp between', value: 'timestamp_bw' },
  ],
} satisfies OperatorGroup;

// Collections does get the email family, despite what a sibling dryad surface
// suggests: the legacy editor mounts CioConditionEditor through the
// compact/collection branch with allowedOperators undefined, so it reaches the
// full default groups (ui's COLLECTION_OPERATOR_FAMILIES declares 'email'), and
// services accepts email_valid for collection_attribute — operatorIsSingleValue
// lists it, decodeOperator handles the '!' prefix.
const emailOperatorGroup = {
  label: 'Email',
  options: [
    { label: 'is valid and exists', value: 'email_valid' },
    { label: "is not valid or doesn't exist", value: '!email_valid' },
  ],
} satisfies OperatorGroup;

export const attributeConditionOperatorGroups: OperatorGroup[] = [
  stringOrNumberOperatorGroup,
  stringOperatorGroup,
  emailOperatorGroup,
  unixTimestampOperatorGroup,
];

const EMAIL_OPERATORS = new Set(emailOperatorGroup.options.map((operator) => operator.value));
const RELATIVE_TIMESTAMP_OPERATORS = new Set(['timestamp_gt', 'timestamp_lt']);
const ABSOLUTE_TIMESTAMP_OPERATOR_TO_RELATIVE: Record<string, string> = {
  timestamp_gta: 'timestamp_gt',
  timestamp_lta: 'timestamp_lt',
};
const RELATIVE_TIMESTAMP_OPERATOR_TO_ABSOLUTE: Record<string, string> = {
  timestamp_gt: 'timestamp_gta',
  timestamp_lt: 'timestamp_lta',
};
const ABSOLUTE_TIMESTAMP_OPERATORS = new Set(Object.keys(ABSOLUTE_TIMESTAMP_OPERATOR_TO_RELATIVE));
export const TIME_DIRECTION_AGO = 'ago';
export const TIME_DIRECTION_FROM_NOW = 'from now';
export const SECONDS_IN_DAY = 86_400;
const SUPPORTED_OPERATORS = new Set([
  ...attributeConditionOperatorGroups.flatMap((group) => group.options.map((option) => option.value)),
  ...ABSOLUTE_TIMESTAMP_OPERATORS,
]);

export function attributeConditionOperatorGroupsForField(field: string): OperatorGroup[] {
  return attributeConditionOperatorGroups.map((group) => {
    if (group.label !== emailOperatorGroup.label) {
      return group;
    }
    return {
      ...group,
      options: group.options.map((operator) => ({ ...operator, isDisabled: field !== 'email' })),
    };
  });
}

function isSupportedAttributeConditionOperator(operator: string): boolean {
  return SUPPORTED_OPERATORS.has(operator);
}

function isBinaryOperator(operator: string): boolean {
  return !['exists', '!exists', 'timestamp', '!timestamp', 'email_valid', '!email_valid'].includes(operator);
}

export function isUnaryFilterOperator(operator: string): boolean {
  return !isBinaryOperator(operator);
}

export function isRelativeTimestampOperator(operator: string): boolean {
  return RELATIVE_TIMESTAMP_OPERATORS.has(operator);
}

export function isAbsoluteTimestampOperator(operator: string): boolean {
  return ABSOLUTE_TIMESTAMP_OPERATORS.has(operator);
}

export function isTimestampInequalityOperator(operator: string): boolean {
  return isRelativeTimestampOperator(operator) || isAbsoluteTimestampOperator(operator);
}

export function normalizeTimestampOperatorForSelect(operator: string): string {
  return ABSOLUTE_TIMESTAMP_OPERATOR_TO_RELATIVE[operator] ?? operator;
}

export function timestampOperatorForVariant(operator: string, variant: 'relative' | 'absolute'): string {
  if (variant === 'absolute') {
    return RELATIVE_TIMESTAMP_OPERATOR_TO_ABSOLUTE[operator] ?? operator;
  }
  return ABSOLUTE_TIMESTAMP_OPERATOR_TO_RELATIVE[operator] ?? operator;
}

export function isTwoValueFilterOperator(operator: string): boolean {
  return operator === 'bw' || operator === 'timestamp_bw';
}

// A relative offset is stored in seconds and shown in whole days. The day count
// is rounded for display only: writing back happens through the setters below,
// so a reader who doesn't touch the row can't turn 3600 into 86400.
export const ONE_DAY_FROM_NOW_SECONDS = String(SECONDS_IN_DAY);

export function relativeTimestampDays(value: string | null): number {
  return Math.abs(Math.round(Number(value ?? '0') / SECONDS_IN_DAY));
}

export function relativeTimestampDirection(value: string | null): string {
  return Number(value ?? '0') < 0 ? TIME_DIRECTION_AGO : TIME_DIRECTION_FROM_NOW;
}

/**
 * Value for a day count the reader typed, keeping the current direction. Typing
 * the count the field already shows leaves the value alone: the display is
 * rounded, so echoing it back would flatten a sub-day offset — clearing the
 * field on a row showing 0 for -3600 would otherwise commit 0. Reaching 0
 * deliberately still works, via any other count and back.
 *
 * A typed count is rounded so the field always stores what it displays; only a
 * value that arrived on the wire can be finer than a day.
 */
export function relativeTimestampForTypedDays(typed: string, value: string | null): string {
  const days = Math.round(Number(typed));

  if (value != null && days === relativeTimestampDays(value)) {
    return value;
  }
  return String(days * SECONDS_IN_DAY * (relativeTimestampDirection(value) === TIME_DIRECTION_AGO ? -1 : 1));
}

/** Seconds for a direction the reader picked, keeping the current magnitude. */
export function relativeTimestampWithDirection(value: string | null, direction: string): string {
  return String(Math.abs(Number(value ?? '0')) * (direction === TIME_DIRECTION_AGO ? -1 : 1));
}

export function isCollectionAttributeFilter(json: unknown): json is Record<string, unknown> & {
  type: typeof COLLECTION_ATTRIBUTE_CONDITION_TYPE;
  field: string;
  operator: string;
} {
  if (!isRecord(json) || json.type !== COLLECTION_ATTRIBUTE_CONDITION_TYPE) {
    return false;
  }
  if (typeof json.field !== 'string' || typeof json.operator !== 'string') {
    return false;
  }
  // Matched whole, not with a leading '!' stripped: every real negated operator
  // is already in the supported set, so stripping would also admit fabrications
  // like '!gt' that no row can render.
  return isSupportedAttributeConditionOperator(json.operator);
}

function conditionValue(operator: string, value: unknown): string | null {
  if (isUnaryFilterOperator(operator) || value == null) {
    return null;
  }
  return String(value);
}

function secondConditionValue(operator: string, value: unknown): string | null {
  return isTwoValueFilterOperator(operator) && value != null ? String(value) : null;
}

function toState(json: unknown): AttributeFilterState | null {
  if (!isCollectionAttributeFilter(json)) {
    return null;
  }
  const operator = json.operator;
  return {
    field: json.field,
    operator,
    value: conditionValue(operator, json.value),
    value2: secondConditionValue(operator, json.value2),
  };
}

function toWire(state: AttributeFilterState): Record<string, unknown> {
  const wire: Record<string, unknown> = {
    type: COLLECTION_ATTRIBUTE_CONDITION_TYPE,
    operator: state.operator,
    field: state.field,
    value: state.value,
  };
  if (isTwoValueFilterOperator(state.operator)) {
    wire.value2 = state.value2;
  }
  return wire;
}

function validateAttributeFilter(state: AttributeFilterState): string[] {
  return [
    validateSelectedField(state),
    validateRequiredValue(state),
    validateAbsoluteTimestamp(state),
    validateRelativeTimestamp(state),
    validateSecondValue(state),
    validateEmailOperator(state),
  ].filter((error): error is string => error != null);
}

export function isValidAttributeFilterState(state: AttributeFilterState): boolean {
  return validateAttributeFilter(state).length === 0;
}

function validateAbsoluteTimestamp(state: AttributeFilterState): string | null {
  if (!isAbsoluteTimestampOperator(state.operator)) {
    return null;
  }
  if (state.value == null || state.value.trim() === '') {
    return 'Choose a timestamp.';
  }
  if (!Number.isInteger(Number(state.value))) {
    return 'Choose a valid timestamp.';
  }
  return null;
}

function validateSelectedField(state: AttributeFilterState): string | null {
  if (state.field.trim() === '') {
    return 'Select an attribute.';
  }
  return null;
}

function validateRequiredValue(state: AttributeFilterState): string | null {
  if (!isUnaryFilterOperator(state.operator) && (state.value == null || state.value.trim() === '')) {
    return 'Enter a value.';
  }
  return null;
}

function validateRelativeTimestamp(state: AttributeFilterState): string | null {
  if (!isRelativeTimestampOperator(state.operator)) {
    return null;
  }
  if (state.value == null || state.value.trim() === '') {
    return 'Enter a timestamp value.';
  }
  if (!Number.isInteger(Number(state.value))) {
    return 'Enter a whole number for the timestamp value.';
  }
  return null;
}

function validateSecondValue(state: AttributeFilterState): string | null {
  if (isTwoValueFilterOperator(state.operator) && (state.value2 == null || state.value2.trim() === '')) {
    return 'Enter a second value.';
  }
  return null;
}

function validateEmailOperator(state: AttributeFilterState): string | null {
  if (EMAIL_OPERATORS.has(state.operator) && state.field !== 'email') {
    return 'Use email operators only with the email attribute.';
  }
  return null;
}

export function isCommittableCollectionAttributeFilter(json: unknown): boolean {
  const state = toState(json);
  return state != null && isValidAttributeFilterState(state);
}

export const collectionAttributePlugin = createPlugin({
  type: COLLECTION_ATTRIBUTE_CONDITION_TYPE,
  label: 'Attribute',
  parse: toState,
  serialize: toWire,
  validate: validateAttributeFilter,
  createDefault: () => ({ field: '', operator: 'eq', value: null, value2: null }),
});

// The wire is a flat AND: services decodes conditions with js.Array() and has
// no group case, so any {and|or: [...]} envelope is a 422 in both directions.
// An {and: [...]} input is still unwrapped for the URLs that carry one. An
// {or: [...]} is declined here, so dryad's built-in grammar (which runs after
// plugin claiming) reads the envelope: the children survive as individual rows
// and the envelope is dropped on serialize — pinned by dryad-round-trip.test.ts.
const flatFilterParseGroup: ParseGroupHook = (json, builtInGroup) => {
  if (Array.isArray(json)) {
    return { operator: 'and', children: json };
  }
  const group = builtInGroup(json);
  return group?.operator === 'and' ? group : null;
};

const flatFilterSerializeGroup: SerializeGroupHook = (_operator, children) => children;

export const flatFilterAdapter = createFormatAdapter({
  plugins: [collectionAttributePlugin],
  // The flat wire is a bare array of leaf wires; members stay unknown because
  // error-node passthrough re-emits whatever arrived.
  wire: wireHelper<unknown[]>(),
  parseGroup: flatFilterParseGroup,
  serializeGroup: flatFilterSerializeGroup,
});

export type CollectionFilterBuilderMeta = {
  isDisabled?: boolean;
  schema: string[];
};

// Every envelope key the wire has ever used, 'or' included — unlike the parse
// grammar, which deliberately knows only about flat arrays and 'and'. The two
// are different questions: this one asks whether a param carries any condition
// worth sending, and an empty group carries none whatever it is spelled.
const GROUP_KEYS = ['and', 'or', 'not'];

export function isEmptyFilters(filters: unknown): boolean {
  if (Array.isArray(filters)) {
    return filters.length === 0 || filters.every(isEmptyFilters);
  }
  if (!isRecord(filters)) {
    return false;
  }
  const groupKey = GROUP_KEYS.find((key) => key in filters);

  return groupKey == null ? Object.keys(filters).length === 0 : isEmptyFilters(filters[groupKey]);
}
